<template>
  <div class="page-header align-items-start min-vh-100" style="
      background-image: url('https://images.unsplash.com/photo-1497294815431-9365093b7331?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1950&q=80');
    ">
    <span class="mask bg-gradient-dark opacity-6"></span>
    <div class="container my-auto">
      <div class="row">
        <div class="col-lg-4 col-md-8 col-12 mx-auto">
          <div class="card z-index-0 fadeIn3 fadeInBottom">
            <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
              <div class="bg-gradient-success shadow-success border-radius-lg py-3 pe-1">
                <h5 class="text-xl text-white font-weight-bolder text-center mt-2 mb-0">
                  {{ currentStep === 'email' ? 'Forgot Password' : currentStep === 'otp' ? 'Verify Your Email' : 'Reset Password' }}
                </h5>
              </div>
            </div>
            <div class="card-body">
              <!-- Email Form -->
              <form v-if="currentStep === 'email'" role="form" class="text-start mt-3" @submit.prevent="handleEmailSubmit">
                <div class="mb-3">
                  <MaterialInput id="firstname" type="text" label="First Name" v-model="firstname" name="firstname" :is-required="true" />
                </div>

                <div class="mb-3">
                  <MaterialInput id="email" type="email" label="Email" v-model="email" name="email" :is-required="true" 
                    :error="!!emailError" @blur="validateEmailField" />
                  <small v-if="emailError" class="text-danger">{{ emailError }}</small>
                </div>

                <div class="text-center">
                  <MaterialButton class="my-4 mb-2" variant="gradient" color="success" fullWidth :disabled="loading">
                    <span v-if="!loading">Send Verification Code</span>
                    <span v-else>Sending...</span>
                  </MaterialButton>
                </div>

                <div class="d-flex align-items-start gap-2 mb-2 px-1">
                  <i class="material-icons text-info" style="font-size: 16px; margin-top: 1px;">help_outline</i>
                  <p class="text-xs text-muted mb-0">We'll send a one-time verification code to your email. Enter the code on the next screen to verify your identity and reset your password.</p>
                </div>

                <p v-if="error" class="text-danger text-center">
                  {{ error }}
                </p>

                <p class="mt-4 text-sm text-center">
                  Remember your password?
                  <router-link :to="{ name: 'SignIn' }" class="text-success text-gradient font-weight-bold">
                    Sign in
                  </router-link>
                </p>
              </form>

              <!-- OTP Form -->
              <form v-if="currentStep === 'otp'" role="form" class="text-start mt-3" @submit.prevent="handleOtpSubmit">
                <div class="mb-3">
                  <label class="form-label d-flex align-items-center mb-3">
                    Enter the 6-digit verification code sent to your email
                  </label>
                  <div class="d-flex justify-content-between gap-2">
                    <input
                      v-for="(digit, index) in otpDigits"
                      :key="index"
                      :ref="el => otpInputs[index] = el"
                      v-model="otpDigits[index]"
                      type="text"
                      maxlength="1"
                      class="form-control text-center"
                      style="width: 40px; height: 40px; font-size: 1.5rem; font-weight: bold;"
                      @input="handleOtpInput(index, $event)"
                      @keydown="handleOtpKeydown(index, $event)"
                      @paste="handleOtpPaste"
                    />
                  </div>
                </div>

                <div class="text-center">
                  <MaterialButton class="my-4 mb-2" variant="gradient" color="success" fullWidth :disabled="loading || !isOtpComplete">
                    <span v-if="!loading">Verify Code</span>
                    <span v-else>Verifying...</span>
                  </MaterialButton>
                </div>

                <p v-if="error" class="text-danger text-center">
                  {{ error }}
                </p>

                <div class="text-center mt-3">
                  <p class="text-sm text-muted">
                    Didn't receive the code?
                    <a href="#" class="text-success text-gradient font-weight-bold" @click.prevent="resendOtp">
                      Resend
                    </a>
                  </p>
                </div>

                <p class="mt-4 text-sm text-center">
                  <a href="#" class="text-success text-gradient font-weight-bold" @click.prevent="backToEmail">
                    Back to Email
                  </a>
                </p>
              </form>

              <!-- Reset Password Form -->
              <form v-if="currentStep === 'reset-password'" role="form" class="text-start mt-3" @submit.prevent="handleResetPassword">
                <div class="mb-3">
                  <MaterialInput
                    id="password"
                    type="password"
                    label="New Password"
                    v-model="password"
                    name="password"
                    :is-required="true"
                  />
                </div>

                <div class="mb-3">
                  <MaterialInput
                    id="password_confirmation"
                    type="password"
                    label="Confirm New Password"
                    v-model="passwordConfirmation"
                    name="password_confirmation"
                    :is-required="true"
                  />
                </div>

                <div class="mb-2">
                  <p class="text-secondary text-sm mb-0">*Password must be at least 6 characters.</p>
                </div>

                <div class="text-center">
                  <MaterialButton class="my-4 mb-2" variant="gradient" color="success" fullWidth :disabled="loading || !isPasswordFormValid">
                    <span v-if="!loading">Reset Password</span>
                    <span v-else>Resetting...</span>
                  </MaterialButton>
                </div>

                <p v-if="error" class="text-danger text-center">
                  {{ error }}
                </p>

                <p class="mt-4 text-sm text-center">
                  <a href="#" class="text-success text-gradient font-weight-bold" @click.prevent="backToEmail">
                    Back to Email
                  </a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>

    <LocationModal v-if="showLocationModal" :modalHeading="'Select Location'" :dropdownList="locationsList"
      @selectedValue="handleLocationSelection" @close="closeLocationModal" />
  </div>
</template>

<script setup>
import { ref, computed, onBeforeMount, onBeforeUnmount, nextTick, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useToast } from 'vue-toast-notification';

import MaterialInput from "@/components/common/MaterialInput.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import LocationModal from "@/components/Modal/LocationModal.vue";
import { isValidEmail } from "@/utils/validation";
import { usePostLogin } from "@/composables/usePostLogin";

const store = useStore();
const router = useRouter();
const toast = useToast();
const { showLocationModal, locationsList, startPostLogin, handleLocationSelection, closeLocationModal } = usePostLogin();

const currentStep = ref("email"); // 'email', 'otp', or 'reset-password'
const email = ref(store.getters.stResetPasswordEmail || "");
const firstname = ref(store.getters.stResetPasswordFirstname || "");
const otpDigits = ref(["", "", "", "", "", ""]);
const otpInputs = ref([]);
const password = ref("");
const passwordConfirmation = ref("");
const error = ref("");
const emailError = ref(null);

// Get loading and error from store
const loading = computed(() => store.getters.stResetPasswordLoading);
const storeError = computed(() => store.getters.stResetPasswordError);

// Watch for store errors and update local error
watch(storeError, (newError) => {
  if (newError) {
    error.value = newError;
    toast.error(newError, { position: 'top-right' });
  }
});

const isOtpComplete = computed(() => {
  return otpDigits.value.every(digit => digit !== "");
});

const isPasswordFormValid = computed(() => {
  return password.value.length >= 6 && 
    password.value === passwordConfirmation.value;
});

const validateEmailField = () => {
  if (email.value && email.value.trim() !== '') {
    if (!isValidEmail(email.value)) {
      emailError.value = 'Please enter a valid email address';
    } else {
      emailError.value = null;
    }
  } else {
    emailError.value = null;
  }
};

const handleEmailSubmit = async () => {
  if (!firstname.value) {
    error.value = "Please enter your first name";
    return;
  }

  if (!email.value) {
    error.value = "Please enter your email";
    return;
  }

  // Validate email using utility
  validateEmailField();
  if (emailError.value) {
    error.value = emailError.value;
    return;
  }
  
  error.value = "";
  
  try {
    // API Call via store: /auth/forgot-password
    await store.dispatch('forgotPassword', {
      email: email.value,
      firstname: firstname.value.trim()
    });

    toast.success('OTP sent to your email address', { position: 'top-right' });
    
    currentStep.value = "otp";
    // Focus first OTP input
    nextTick(() => {
      if (otpInputs.value[0]) {
        otpInputs.value[0].focus();
      }
    });
  } catch (err) {
    console.error("Forgot password error:", err);
    // Error is already handled by store and watch
  }
};

const handleOtpInput = (index, event) => {
  const value = event.target.value.replace(/[^0-9]/g, "");
  otpDigits.value[index] = value;
  
  // Auto-focus next input
  if (value && index < 5) {
    nextTick(() => {
      if (otpInputs.value[index + 1]) {
        otpInputs.value[index + 1].focus();
      }
    });
  }
};

const handleOtpKeydown = (index, event) => {
  // Handle backspace
  if (event.key === "Backspace" && !otpDigits.value[index] && index > 0) {
    nextTick(() => {
      if (otpInputs.value[index - 1]) {
        otpInputs.value[index - 1].focus();
      }
    });
  }
  
  // Handle arrow keys
  if (event.key === "ArrowLeft" && index > 0) {
    event.preventDefault();
    nextTick(() => {
      if (otpInputs.value[index - 1]) {
        otpInputs.value[index - 1].focus();
      }
    });
  }
  
  if (event.key === "ArrowRight" && index < 5) {
    event.preventDefault();
    nextTick(() => {
      if (otpInputs.value[index + 1]) {
        otpInputs.value[index + 1].focus();
      }
    });
  }
};

const handleOtpPaste = (event) => {
  event.preventDefault();
  const pastedData = event.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 6);
  
  for (let i = 0; i < 6; i++) {
    otpDigits.value[i] = pastedData[i] || "";
  }
  
  // Focus the next empty input or the last one
  const nextEmptyIndex = otpDigits.value.findIndex(digit => digit === "");
  const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
  nextTick(() => {
    if (otpInputs.value[focusIndex]) {
      otpInputs.value[focusIndex].focus();
    }
  });
};

const handleOtpSubmit = async () => {
  if (!isOtpComplete.value) {
    error.value = "Please enter complete OTP";
    return;
  }
  
  error.value = "";
  
  // Combine OTP digits
  const otp = otpDigits.value.join("");
  
  try {
    // API Call via store: /api/auth/verify-otp
    await store.dispatch('verifyOtp', otp);

    toast.success('OTP verified successfully', { position: 'top-right' });
    
    // Move to reset password step
    currentStep.value = "reset-password";
  } catch (err) {
    console.error("Verify OTP error:", err);
    // Error is already handled by store and watch
    
    // Clear OTP inputs on error
    otpDigits.value = ["", "", "", "", "", ""];
    nextTick(() => {
      if (otpInputs.value[0]) {
        otpInputs.value[0].focus();
      }
    });
  }
};

const resendOtp = async () => {
  // Reset OTP inputs
  otpDigits.value = ["", "", "", "", "", ""];
  error.value = "";
  
  try {
    // API Call via store: /auth/forgot-password (resend)
    await store.dispatch('forgotPassword', {
      email: email.value,
      firstname: firstname.value.trim()
    });

    toast.success('OTP resent to your email address', { position: 'top-right' });
    
    nextTick(() => {
      if (otpInputs.value[0]) {
        otpInputs.value[0].focus();
      }
    });
  } catch (err) {
    console.error("Resend OTP error:", err);
    // Error is already handled by store and watch
  }
};

const handleResetPassword = async () => {
  if (!isPasswordFormValid.value) {
    if (password.value.length < 6) {
      error.value = "Password must be at least 6 characters";
    } else if (password.value !== passwordConfirmation.value) {
      error.value = "Passwords do not match";
    } else {
      error.value = "Please fill in all fields";
    }
    return;
  }

  error.value = "";

  try {
    // Combine OTP digits
    const otp = otpDigits.value.join("");

    // API Call via store: /api/auth/reset-password
    const result = await store.dispatch('resetPassword', {
      otp: otp,
      password: password.value,
      password_confirmation: passwordConfirmation.value
    });

    if (result?.output?.access_token) {
      toast.success('Password reset successfully! Signing you in...', { position: 'top-right' });
      try {
        await startPostLogin(result);
      } catch (navError) {
        console.error("Auto sign-in navigation failed", navError);
        router.push({ name: 'SignIn' });
      }
      return;
    }

    toast.success('Password reset successfully! Redirecting to login...', { position: 'top-right' });
    
    // Redirect to sign in page after a short delay
    setTimeout(() => {
      router.push({ name: 'SignIn' });
    }, 2000);
  } catch (err) {
    console.error("Reset password error:", err);
    // Error is already handled by store and watch
  }
};

const backToEmail = () => {
  currentStep.value = "email";
  otpDigits.value = ["", "", "", "", "", ""];
  password.value = "";
  passwordConfirmation.value = "";
  error.value = "";
  // Keep email and firstname when going back
};

onBeforeMount(() => {
  store.commit("toggleEveryDisplay");
  store.commit("toggleHideConfig");
});

onBeforeUnmount(() => {
  store.commit("toggleEveryDisplay");
  store.commit("toggleHideConfig");
});
</script>

<style scoped>
.form-control:focus {
  border-color: #4caf50;
  box-shadow: 0 0 0 0.2rem rgba(76, 175, 80, 0.25);
}
</style>

